/**
 * Concurrent-request de-duplication for `createAsyncThunk` payload creators.
 *
 * Step 8 of `docs/api-call-volume-audit.md`. The globally-shared, argument-less
 * reads are dispatched from everywhere - `fetchUserDetails` from 77 sites,
 * `fetchOrganizationConfigs` from 67 - and a cold load or a route mount fires
 * several of them in the same tick. Nothing collapsed them, so each one was a
 * separate round trip for identical data.
 *
 * **Why not `condition`.** RTK's own answer is `condition`, but a thunk whose
 * `condition` returns false *rejects* its promise. This codebase has 618
 * `.unwrap()` call sites, many shaped `dispatch(fetch...()).unwrap().then(...)`,
 * and all of them would start taking the failure path whenever they happened to
 * lose the race. Sharing the in-flight promise instead means every duplicate
 * dispatch resolves with the same data it would have fetched for itself - the
 * request count drops, and no caller can tell the difference.
 *
 * Both dispatches still run through the reducer (one `pending` and one
 * `fulfilled` each). These reducers only assign, so that is idempotent.
 *
 * Deliberately **not** a TTL cache. Until the mutation reducers write their
 * server response into state (step 9 of the audit), the app relies on
 * `dispatch(update...()).unwrap().then(() => dispatch(fetch...()))` to see its
 * own writes, and a TTL would serve the pre-write value back.
 */

/**
 * Counter of mutations the app has issued, bumped by the axios request
 * interceptor. See {@link bumpWriteEpoch}.
 */
let writeEpoch = 0;

/**
 * Marks that a mutation has been sent, invalidating every in-flight read for
 * sharing purposes.
 *
 * Sharing an in-flight GET is only safe while nothing has been written since it
 * was issued. Without this, a caller doing the very common
 * `dispatch(update...()).unwrap().then(() => dispatch(fetch...()))` could be
 * handed a read that started *before* its own write and see the pre-write
 * value. The onboarding steps advance on exactly that pattern, so a stale read
 * there would stall the wizard.
 *
 * Bumping at request time rather than on the response is deliberate: a mutation
 * that is merely in flight must already stop sharing, because a read issued
 * before it cannot reflect it.
 *
 * @returns {void}
 */
export const bumpWriteEpoch = () => {
  writeEpoch += 1;
};

/** @returns {number} the current write epoch */
export const getWriteEpoch = () => writeEpoch;

/**
 * Wraps a payload creator so that dispatches sharing a key share one request.
 *
 * Only requests issued in the current write epoch are shared, so a read
 * dispatched after a mutation always goes to the network.
 *
 * @template T
 * @param {(arg: any) => (string|null)} buildKey maps the thunk argument to a
 *   dedupe key; return `null` to opt a particular call out of sharing
 * @param {(arg: any, thunkApi: object) => Promise<T>} payloadCreator the
 *   original creator
 * @returns {(arg: any, thunkApi: object) => Promise<T>} the wrapped creator
 */
export const withRequestDedupe = (buildKey, payloadCreator) => {
  /** @type {Map<string, {promise: Promise<any>, epoch: number}>} */
  const inFlight = new Map();

  return (arg, thunkApi) => {
    const key = buildKey(arg);
    if (key === null || key === undefined) {
      return payloadCreator(arg, thunkApi);
    }

    const existing = inFlight.get(key);
    // A request issued before the most recent write cannot reflect it, so it is
    // no longer a valid stand-in for this dispatch.
    if (existing && existing.epoch === getWriteEpoch()) {
      return existing.promise;
    }

    // `Promise.resolve` normalizes a creator that returns a plain value.
    // A rejection is shared too, which is correct: the duplicate dispatches
    // were asking for the request that just failed.
    const promise = Promise.resolve(payloadCreator(arg, thunkApi));
    const entry = { promise, epoch: getWriteEpoch() };
    inFlight.set(key, entry);

    // Only clear if we are still the current entry, so a request started after
    // this one settled is not evicted by our cleanup.
    const settle = () => {
      if (inFlight.get(key) === entry) inFlight.delete(key);
    };
    // Attached directly to `promise`, with both handlers in a single `.then`,
    // and before the caller gets a chance to attach its own. A
    // `.catch().finally()` chain runs two microtasks later than the caller's
    // `await`, so code doing `await dispatch(read()); dispatch(read());` would
    // find the settled entry still in the map and be handed the previous,
    // already-resolved value. That would be a cache, and this is not one.
    promise.then(settle, settle);
    return promise;
  };
};

/** Dedupe key for thunks that take no meaningful argument. */
export const SINGLETON_KEY = () => 'singleton';

export default withRequestDedupe;
